// Score track (ARCHITECTURE §4): the weighted mean of the five victory tracks, with weights the
// viewer edits on the Victory screen and keeps in localStorage (a per-viewer convenience).
//
//   score(nation) = Σ wₜ · progressₜ / Σ wₜ   over tracks with wₜ > 0 (each progress is 0..1:
//   rungs ÷ 9 for space, an index for diplomatic, shares for the rest; the result is no share),
//   only for nations present in every weighted track; data year = the oldest input's year.

import type { SourceId } from '../core/schema.ts';
import type { VictoryEntry, VictoryTrack } from '../world/api.ts';
import { fmtTrack } from './tracks.ts';

export type BaseTrack = Exclude<VictoryTrack, 'score'>;
export type Weights = Record<BaseTrack, number>;

export const BASE_TRACKS: BaseTrack[] = ['space', 'tech', 'economic', 'diplomatic', 'culture'];

const WEIGHTS_KEY = 'worldciv.ui.scoreWeights';
export const WEIGHT_MAX = 5;
export const DEFAULT_WEIGHTS: Weights = { space: 1, tech: 1, economic: 1, diplomatic: 1, culture: 1 };

export function loadWeights(): Weights {
  try {
    const raw = localStorage.getItem(WEIGHTS_KEY);
    if (!raw) {
      return { ...DEFAULT_WEIGHTS };
    }
    const parsed = JSON.parse(raw) as Partial<Record<BaseTrack, number>>;
    const out = { ...DEFAULT_WEIGHTS };
    for (const t of BASE_TRACKS) {
      const v = parsed[t];
      if (typeof v === 'number' && v >= 0 && v <= WEIGHT_MAX) {
        out[t] = v;
      }
    }
    return out;
  } catch {
    return { ...DEFAULT_WEIGHTS };
  }
}

export function saveWeights(w: Weights): void {
  try {
    localStorage.setItem(WEIGHTS_KEY, JSON.stringify(w));
  } catch {
    // Storage blocked (private window, preview): weights last for this session only.
  }
}

/** Score = Σ wₜ·progressₜ / Σ wₜ over nations present in every weighted track. */
export function scoreOf(perTrack: Record<BaseTrack, VictoryEntry[]>, w: Weights, names: Record<BaseTrack, string>): VictoryEntry[] {
  const used = BASE_TRACKS.filter((t) => w[t] > 0);
  const wsum = used.reduce((s, t) => s + w[t], 0);
  if (wsum === 0) {
    return [];
  }

  const byIso = new Map<string, { sum: number; n: number; year: number; sources: Set<SourceId>; parts: string[] }>();
  for (const t of used) {
    for (const e of perTrack[t]) {
      let acc = byIso.get(e.iso3);
      if (!acc) {
        acc = { sum: 0, n: 0, year: e.dataYear, sources: new Set(), parts: [] };
        byIso.set(e.iso3, acc);
      }
      acc.sum += w[t] * e.progress;
      acc.n += 1;
      acc.year = Math.min(acc.year, e.dataYear);
      e.sources.forEach((s) => acc.sources.add(s));
      acc.parts.push(`${names[t]} ${fmtTrack(t, e.progress)} (${e.dataYear})`);
    }
  }

  const out: VictoryEntry[] = [];
  for (const [iso3, acc] of byIso) {
    if (acc.n < used.length) {
      continue;
    }
    out.push({ iso3, progress: acc.sum / wsum, detail: acc.parts.join(' · '), dataYear: acc.year, sources: [...acc.sources] });
  }
  return out.sort((a, b) => b.progress - a.progress);
}
